import React, { useMemo } from 'react';
import { FaPlus } from 'react-icons/fa';
import { AdminLayout } from '../components/layout';
import { AdminTable } from '../components/admin';
import { SearchBar } from '../components/ui';
import { GenericForm } from '../components/forms';
import { ErrorDisplay } from '../components/ui';
import { useCrud, useForm, useTableFilter, useConfirm, useDepartmentOffice } from '../hooks';
import { useNotifications } from '../context/NotificationContext';
import {
  UserData,
  emptyUser,
  userCrudOperations,
  userFormFields,
  userTableColumns,
  userSearchableFields,
} from '../config/userConfig';
import { TableSkeleton } from '../components/common/state/SkeletonComponents';

const UserManagement: React.FC = () => {
  // Generic CRUD operations
  const crud = useCrud(userCrudOperations, 'kullanıcı');

  // Generic form management
  const form = useForm(emptyUser);

  // Generic table filtering
  const { searchTerm, setSearchTerm, filteredData } = useTableFilter(crud.items, {
    searchableFields: userSearchableFields,
  });

  // Modern confirm dialog for delete operations
  const { confirm, ConfirmComponent } = useConfirm();

  // Notifications for success/error messages
  const { showSuccess, showError } = useNotifications();

  // Department and office data
  const { departments, offices } = useDepartmentOffice(
    form.isOpen,
    form.data.management || '',
    form.data.office || ''
  );

  // Update form fields with dynamic options
  const dynamicFormFields = useMemo(() => {
    return userFormFields.map(field => {
      let fieldConfig = { ...field };
      if (field.key === 'management') {
        fieldConfig.options = departments.map(dept => ({
          value: dept.name,
          label: dept.name,
        }));
      }
      if (field.key === 'office') {
        fieldConfig.options = offices.map(office => ({
          value: office.name,
          label: office.name,
        }));
      }
      // Sadece iş bilgileri düzenlenebilir, diğerleri edit modunda disabled
      if (
        form.mode === 'edit' &&
        !['management', 'office'].includes(field.key)
      ) {
        fieldConfig.disabled = true;
      }
      return fieldConfig;
    });
  }, [departments, offices, form.mode]);

  // Handle department change to reset office
  const handleFieldChange = (field: keyof UserData, value: any) => {
    if (field === 'management') {
      // Reset office when department changes
      form.updateField('office', '');
    }
    form.updateField(field, value);
  };

  // Handle form submission
  const handleFormSubmit = async () => {
    try {
      if (form.mode === 'create') {
        await crud.handleCreate(form.data as Omit<UserData, 'id'>);
        showSuccess('Başarılı!', 'Kullanıcı başarıyla eklendi.');
      } else {
        await crud.handleUpdate(form.data as UserData);
        showSuccess('Başarılı!', 'Kullanıcı başarıyla güncellendi.');
      }
      form.close();
    } catch (error) {
      showError('Hata!', 'İşlem sırasında bir hata oluştu.');
      // Error is also handled by the CRUD hook
    }
  };

  return (
    <AdminLayout title="Kullanıcı Yönetimi">
      <ErrorDisplay error={crud.error} onDismiss={crud.clearError} className="mb-4" />

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <SearchBar value={searchTerm} onChange={setSearchTerm} placeholder="Kullanıcı ara..." />
        <button
          onClick={form.openCreate}
          className="flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 transition shadow-md hover:shadow-lg"
          disabled={crud.loading}
        >
          <FaPlus /> Yeni Kullanıcı
        </button>
      </div>

      {crud.loading ? (
        <TableSkeleton rows={8} columns={6} />
      ) : (
        <AdminTable<UserData>
          columns={userTableColumns}
          data={filteredData}
          onEdit={form.openEdit}
          // onConfirmDelete={handleDelete} // Kullanıcı silme butonu kaldırıldı
        />
      )}

      <GenericForm<UserData>
        isOpen={form.isOpen}
        onClose={form.close}
        title={form.mode === 'create' ? 'Yeni Kullanıcı Ekle' : 'Kullanıcı Düzenle'}
        data={form.data}
        fields={dynamicFormFields}
        onFieldChange={handleFieldChange}
        onSave={handleFormSubmit}
        saveText={form.mode === 'create' ? 'Ekle' : 'Güncelle'}
      />

      {/* Modal Confirm Component */}
      {ConfirmComponent}
    </AdminLayout>
  );
};

export default UserManagement;
